import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ChevronDown, Crown } from "lucide-react";
import { useLang } from "../context/LangContext";
import { IMAGES } from "../images";
import { scrollToId, waLink } from "../constants";
import { WhatsAppIcon } from "./icons";
import { SlimCountdown } from "./Countdown";

const PlatformMarquee = () => {
  const { t } = useLang();
  const items = t.marquee;
  return (
    <div className="marquee-track marquee-mask overflow-hidden border-y border-white/5 bg-white/[0.02] py-5">
      <div className="animate-marquee flex w-max">
        {[0, 1].map((dup) => (
          <div key={dup} aria-hidden={dup === 1} className="flex items-center">
            {items.map((item, i) => (
              <div key={`${dup}-${i}`} className="mx-7 flex items-center gap-2.5 text-sm font-semibold tracking-wide text-slate-400">
                <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-blue-400 to-violet-400" />
                <span className="whitespace-nowrap">{item}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 60, damping: 16 });
  const sry = useSpring(ry, { stiffness: 60, damping: 16 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 5);
    rx.set(-py * 4);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <section id="top" className="relative overflow-hidden">
      {/* ambient background */}
      <div className="bg-grid pointer-events-none absolute inset-0" />
      <div className="animate-float-slow pointer-events-none absolute -top-32 left-1/2 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-blue-600/20 blur-[140px]" />
      <div className="animate-pulse-glow pointer-events-none absolute right-[-120px] top-1/3 h-[380px] w-[380px] rounded-full bg-violet-600/15 blur-[120px]" />

      <div className="relative mx-auto flex max-w-7xl flex-col px-5 pb-10 pt-24 sm:px-8 lg:pt-32">
        {/* mobile headline block — first screen essentials */}
        <div className="order-1 lg:hidden">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3.5 py-1.5 text-[11px] font-bold tracking-wider text-amber-300"
          >
            <Crown className="h-3.5 w-3.5" />
            {t.hero.badge} · {t.hero.badgeSub}
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.08 }}
            className="mt-4 font-display text-4xl font-black leading-[1.12] text-white"
          >
            {t.hero.h1a}
            {t.hero.h1b && (
              <>
                <br />
                <span className="text-gradient">{t.hero.h1b}</span>
              </>
            )}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.16 }}
            className="mt-3 text-sm leading-relaxed text-slate-400"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.24 }}
            className="mt-5 flex flex-col gap-3 sm:flex-row"
          >
            <a
              href={waLink(t.contact.msgDefault)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-green-600 to-green-500 px-6 py-3.5 text-sm font-bold text-white shadow-[0_0_28px_rgba(34,197,94,0.4)] transition-transform duration-300 hover:scale-[1.03]"
            >
              <WhatsAppIcon className="h-5 w-5" />
              {t.hero.ctaPrimary}
            </a>
            <button
              data-testid="hero-view-pricing-btn"
              onClick={() => scrollToId("pricing")}
              className="flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-semibold text-slate-200 backdrop-blur transition-colors hover:border-white/30 hover:text-white"
            >
              {t.hero.ctaSecondary}
              <ChevronDown className="h-4 w-4" />
            </button>
          </motion.div>
        </div>

        {/* slim countdown strip */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="order-2 mt-5 flex justify-center lg:mt-4"
        >
          <SlimCountdown />
        </motion.div>

        {/* desktop eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="order-1 mb-2 hidden justify-center lg:mb-0 lg:flex"
        >
          <div className="glass flex items-center gap-3 rounded-full px-5 py-2.5">
            <span className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-2.5 py-1 text-[10px] font-black tracking-wider text-slate-950">
              <Crown className="h-3 w-3" />
              {t.hero.badge}
            </span>
            <span className="text-xs font-medium tracking-wide text-slate-300">{t.hero.pill}</span>
          </div>
        </motion.div>

        {/* hero visual — final branded banner */}
        <motion.div
          className="order-3 mt-5 lg:mt-4"
          initial={{ opacity: 0, scale: 0.965 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            style={{ rotateX: srx, rotateY: sry, transformPerspective: 1400 }}
            className="group relative"
          >
            <div className="absolute -inset-6 rounded-[36px] bg-gradient-to-r from-blue-600/30 via-violet-600/25 to-cyan-500/20 blur-3xl" />
            <div className="glow-card relative overflow-hidden rounded-2xl shadow-[0_24px_80px_rgba(2,6,23,0.8)] lg:rounded-[28px]">
              <img
                src={IMAGES[lang].hero.src}
                width={IMAGES[lang].hero.w}
                height={IMAGES[lang].hero.h}
                alt="MB PAY VIA — High Volume Virtual Card Exclusive Rates"
                className="block h-auto w-full"
                loading="eager"
                data-testid="hero-visual"
              />
              <div className="pointer-events-none absolute inset-0 rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]" />
            </div>
          </motion.div>
        </motion.div>

        {/* desktop CTA row under visual */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45 }}
          className="order-4 mt-10 hidden items-center justify-center gap-4 lg:flex"
        >
          <a
            href={waLink(t.contact.msgDefault)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-desktop-whatsapp"
            className="flex items-center gap-2.5 rounded-full bg-gradient-to-r from-green-600 to-green-500 px-8 py-4 text-base font-bold text-white shadow-[0_0_36px_rgba(34,197,94,0.35)] transition-transform duration-300 hover:scale-[1.04]"
          >
            <WhatsAppIcon />
            {t.hero.whatsappBtn}
          </a>
          <a
            href="https://t.me/Mbglobal96"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-desktop-telegram"
            className="flex items-center gap-2.5 rounded-full bg-gradient-to-r from-sky-600 to-blue-500 px-8 py-4 text-base font-bold text-white shadow-[0_0_36px_rgba(56,160,240,0.35)] transition-transform duration-300 hover:scale-[1.04]"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
              <path d="M23.91 3.79 20.3 20.84c-.25 1.21-.98 1.5-2 .94l-5.5-4.07-2.66 2.57c-.3.3-.55.56-1.1.56-.72 0-.6-.27-.84-.95L6.3 13.7l-5.45-1.7c-1.18-.35-1.19-1.16.26-1.75l21.26-8.2c.97-.43 1.9.24 1.53 1.73z" />
            </svg>
            {t.hero.telegramBtn}
          </a>
        </motion.div>

        {/* mobile lock line */}
        <div className="order-5 mt-6 lg:hidden">
          <p className="text-center text-xs leading-relaxed text-slate-500">{t.hero.lockLine}</p>
        </div>
      </div>

      <div className="order-6 relative mt-6">
        <PlatformMarquee />
      </div>
    </section>
  );
};
